import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Linkedin,
  Mail,
  MessageCircle,
  Send,
} from 'lucide-react';
import { SEOHead } from '../Components/common/SEOHead';
import './Contact.css';

export function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [canSubmit, setCanSubmit] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const timer = window.setTimeout(() => setCanSubmit(true), 2000);
    return () => window.clearTimeout(timer);
  }, []);

  const validateForm = (formData: FormData) => {
    const errors: Record<string, string> = {};
    const email = String(formData.get('email') || '');
    const name = String(formData.get('name') || '');
    const message = String(formData.get('message') || '');

    if (name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters long';
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters long';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setValidationErrors({});

    const form = event.currentTarget;
    const formData = new FormData(form);

    if (!validateForm(formData)) {
      setIsSubmitting(false);
      return;
    }

    try {
      formData.append('submitted_at', new Date().toISOString());

      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        window.setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <SEOHead
        title="Contact Us - Get Support & Share Feedback"
        description="Need help with our Power BI visuals? Have questions or feedback? Contact BasedOn support team. We respond within 24 hours on business days."
        keywords="contact BasedOn, Power BI support, custom visual support, feedback, get help"
        canonical="https://based-on.net/contact"
      />

      <section className="contact-hero">
        <div className="contact-dot-grid contact-dot-grid-left" aria-hidden="true" />
        <div className="contact-dot-grid contact-dot-grid-right" aria-hidden="true" />
        <div className="contact-wave" aria-hidden="true" />

        <motion.div
          className="contact-hero-content"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <div className="contact-pill">
            <MessageCircle size={15} />
            <span>Let's Talk</span>
          </div>
          <h1>Get in Touch</h1>
          <div className="contact-title-mark" />
          <p>
            Questions, feedback, feature requests, or support?
            <br />
            Whether you're evaluating a visual, need help with implementation,
            <br />
            or have an idea for improvement, feel free to reach out.
          </p>
        </motion.div>
      </section>

      <section className="contact-main">
        <motion.div
          className="contact-grid"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <div className="contact-card contact-form-card">
            <h2>Send a Message</h2>
            <p className="contact-card-subtitle">Every message is read and answered personally.</p>

            <form
              className="contact-form"
              action="https://getform.io/f/bnlnzzob"
              method="POST"
              onSubmit={handleSubmit}
            >
              <div className="contact-form-row">
                <div className="contact-field">
                  <label htmlFor="contact-name">Name <span>*</span></label>
                  <input
                    id="contact-name"
                    name="name"
                    placeholder="Your name"
                    required
                    minLength={2}
                    maxLength={100}
                    aria-invalid={!!validationErrors.name}
                    aria-describedby={validationErrors.name ? 'contact-name-error' : undefined}
                  />
                  {validationErrors.name && (
                    <p id="contact-name-error" className="contact-error">{validationErrors.name}</p>
                  )}
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-email">Email <span>*</span></label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    required
                    aria-invalid={!!validationErrors.email}
                    aria-describedby={validationErrors.email ? 'contact-email-error' : undefined}
                  />
                  {validationErrors.email && (
                    <p id="contact-email-error" className="contact-error">{validationErrors.email}</p>
                  )}
                </div>
              </div>

              <div className="contact-field">
                <label htmlFor="contact-company">Company (Optional)</label>
                <input
                  id="contact-company"
                  name="company"
                  placeholder="Your company name"
                  maxLength={100}
                />
              </div>

              <div className="contact-field">
                <label htmlFor="contact-message">Message <span>*</span></label>
                <textarea
                  id="contact-message"
                  name="message"
                  placeholder="Tell us about your Power BI challenges or how we can help..."
                  required
                  minLength={10}
                  maxLength={1000}
                  aria-invalid={!!validationErrors.message}
                  aria-describedby={validationErrors.message ? 'contact-message-error' : undefined}
                />
                {validationErrors.message && (
                  <p id="contact-message-error" className="contact-error">{validationErrors.message}</p>
                )}
              </div>

              <input
                className="contact-honeypot"
                type="text"
                name="_gotcha"
                tabIndex={-1}
                autoComplete="off"
              />

              <button
                className="contact-submit"
                type="submit"
                disabled={isSubmitting || !canSubmit}
              >
                <Send size={18} />
                <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
              </button>

              {submitStatus === 'success' && (
                <div className="contact-status contact-status-success">
                  <CheckCircle2 size={18} />
                  <p>Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="contact-status contact-status-error">
                  <AlertCircle size={18} />
                  <p>Something went wrong. Please try again or email us directly at support@based-on.net</p>
                </div>
              )}
            </form>
          </div>

          <aside className="contact-info-stack" aria-label="Contact information">
            <div className="contact-card contact-info-card">
              <div className="contact-icon contact-icon-warm">
                <Mail size={34} />
              </div>
              <h3>Email</h3>
              <p>The best way to reach me.</p>
              <a href="mailto:support@based-on.net">support@based-on.net</a>
            </div>

            <div className="contact-card contact-info-card">
              <div className="contact-icon contact-icon-cool">
                <Clock size={34} />
              </div>
              <h3>Response Time</h3>
              <p>Typically within 24 hours on business days.</p>
              <div className="contact-response-note">
                <CheckCircle2 size={18} />
                <span>Within 24 hours</span>
              </div>
            </div>
          </aside>
        </motion.div>

        <motion.section
          className="contact-card contact-author-card"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <div className="contact-author-photo-wrap">
            <div className="contact-dot-grid contact-author-dots" aria-hidden="true" />
            <img src="/assets/iuliia.png" alt="Iuliia Strunnikova" className="contact-author-photo" />
          </div>

          <div className="contact-author-content">
            <p className="contact-eyebrow">About the Author</p>
            <h2>Iuliia Strunnikova</h2>
            <p className="contact-author-role">Developer &amp; Creator</p>
            <div className="contact-author-mark" />
            <p className="contact-author-copy">
              I build data visualization tools that are clean, efficient, and easy to use.
              My background includes experience in modern web technologies and backend development.
            </p>

            <a
              className="contact-linkedin"
              href="https://www.linkedin.com/in/iuliia-strunnikova/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Linkedin size={18} />
              <span>Connect on LinkedIn</span>
              <ExternalLink size={15} />
            </a>
          </div>
        </motion.section>
      </section>
    </div>
  );
}
